import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { getActivitySuggestions, getCategorySuggestions, getCategoryLabel, debounce } from '@/utils/suggestionUtils';
import { getActivityCategoryLabel } from '@/services/activityService';
import { Activity, ActivityCategory } from '@/types';

interface ActivityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const ActivityAutocomplete: React.FC<ActivityAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Rechercher une activité...',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [activitySuggestions, setActivitySuggestions] = useState<Activity[]>([]);
  const [categorySuggestions, setCategorySuggestions] = useState<ActivityCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search function
  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      setIsLoading(true);
      
      try {
        // Get suggestions (getActivitySuggestions is now async)
        const activities = await getActivitySuggestions(query, 5);
        const categories = getCategorySuggestions(query);

        setActivitySuggestions(activities);
        setCategorySuggestions(categories);

        // Open dropdown if we have suggestions
        if (activities.length > 0 || categories.length > 0) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setActivitySuggestions([]);
        setCategorySuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  // Update suggestions when value changes
  useEffect(() => {
    if (value.length >= 2) {
      debouncedSearch(value);
    } else {
      setActivitySuggestions([]);
      setCategorySuggestions([]);
      setOpen(false);
      setIsLoading(false);
    }
  }, [value, debouncedSearch]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasSuggestions = activitySuggestions.length > 0 || categorySuggestions.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={`flex items-center px-4 py-2 bg-gray-50 rounded-lg ${className}`}>
          <Search size={20} className="text-gray-400 mr-2 flex-shrink-0" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (hasSuggestions) {
                setOpen(true);
              }
            }}
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={open ? 'activity-suggestions' : undefined}
            aria-label="Rechercher une activité"
          />
        </div>
      </PopoverTrigger>

      {(hasSuggestions || isLoading) && (
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0" 
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandList id="activity-suggestions" role="listbox">
              {isLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  Recherche en cours...
                </div>
              ) : (
                <>
                  {!hasSuggestions && (
                    <CommandEmpty>Aucune suggestion</CommandEmpty>
                  )}

                  {activitySuggestions.length > 0 && (
                    <CommandGroup heading="Activités">
                      {activitySuggestions.map((activity) => (
                        <CommandItem
                          key={activity.id}
                          onSelect={() => handleSelect(activity.name)}
                          className="cursor-pointer min-h-[44px] py-2"
                          role="option"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{activity.name}</span>
                            <span className="text-xs text-gray-500">
                              {getActivityCategoryLabel(activity)} • {activity.city}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {categorySuggestions.length > 0 && (
                    <CommandGroup heading="Catégories">
                      {categorySuggestions.map((category) => (
                        <CommandItem
                          key={category}
                          onSelect={() => handleSelect(getCategoryLabel(category))}
                          className="cursor-pointer min-h-[44px]"
                          role="option"
                        >
                          <span className="font-medium">{getCategoryLabel(category)}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default ActivityAutocomplete;

